import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Book, ChevronRight, Menu, X, ArrowLeft, Search } from 'lucide-react';

// Documentation structure
const docSections = [
  { id: 'readme', title: 'Overview', file: 'README.md' },
  { id: 'getting-started', title: 'Getting Started', file: '01-getting-started.md' },
  { id: 'cloudapi', title: 'CloudAPI Overview', file: '02-cloudapi.md' },
  { id: 'instances', title: 'Instances', file: '03-instances.md' },
  { id: 'images', title: 'Images', file: '04-images.md' },
  { id: 'networks', title: 'Networks', file: '05-networks.md' },
  { id: 'volumes', title: 'Volumes', file: '06-volumes.md' },
  { id: 'firewall', title: 'Firewall', file: '07-firewall.md' },
  { id: 'rbac', title: 'RBAC', file: '08-rbac.md' },
  { id: 'docker-api', title: 'Docker API', file: '09-docker-api.md' },
  { id: 'authentication', title: 'Authentication', file: '10-authentication.md' },
  { id: 'cli-reference', title: 'CLI Reference', file: '11-cli-reference.md' },
];

export const TritonDocsPage = () => {
  const [activeSection, setActiveSection] = useState('readme');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      const section = docSections.find(s => s.id === activeSection);
      if (section) {
        try {
          const response = await fetch(`/triton-docs/${section.file}`);
          const text = await response.text();
          setContent(text);
        } catch (error) {
          setContent('# Error\n\nFailed to load documentation.');
        }
      }
      setLoading(false);
    };
    loadContent();
  }, [activeSection]);

  const filteredSections = docSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-[var(--bg-primary)] min-h-screen pt-20">
      {/* Header */}
      <div className="bg-[var(--bg-secondary)] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-white/70 hover:text-white"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#2a5ff6]/20 flex items-center justify-center rounded-lg">
                <Book size={18} className="text-[#2a5ff6]" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">API Documentation</h1>
                <p className="text-xs text-white/50">Version 9.15.0</p>
              </div>
            </div>
          </div>
          <Link 
            to="/" 
            className="hidden md:flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky lg:top-20 left-0 z-30 h-screen lg:h-[calc(100vh-5rem)] w-72
          bg-[var(--bg-secondary)] lg:bg-transparent border-r border-white/10 lg:border-r-0 overflow-y-auto
          transition-transform duration-300 lg:translate-x-0 pt-4
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-4">
            {/* Search */}
            <div className="relative mb-6">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#2a5ff6]/50"
              />
            </div>

            {/* Navigation */}
            <nav className="space-y-1">
              {filteredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-[#2a5ff6]/20 text-[#2a5ff6]'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span>{section.title}</span>
                  {activeSection === section.id && <ChevronRight size={16} />}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-8rem)] px-4 md:px-8 lg:px-12 py-8 lg:py-12">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#2a5ff6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <article className="prose prose-invert prose-lg max-w-4xl">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 pb-4 border-b border-white/10">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-2xl font-semibold text-white mt-10 mb-4">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-xl font-semibold text-white mt-8 mb-3">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-white/80 leading-relaxed mb-4">
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside text-white/80 space-y-2 mb-4">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-white/80">{children}</li>
                  ),
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      className="text-[#2a5ff6] hover:underline"
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {children}
                    </a>
                  ),
                  code: ({ inline, className, children }) => {
                    if (inline) {
                      return (
                        <code className="bg-white/10 text-[#2a5ff6] px-1.5 py-0.5 rounded text-sm font-mono">
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code className={className}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-[#0d1526] border border-white/10 rounded-xl p-4 overflow-x-auto mb-6">
                      <code className="text-sm font-mono text-white/90">
                        {children}
                      </code>
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-[#2a5ff6] pl-4 italic text-white/70 my-4">
                      {children}
                    </blockquote>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto mb-6">
                      <table className="w-full border-collapse">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-white/5">
                      {children}
                    </thead>
                  ),
                  th: ({ children }) => (
                    <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-white/10 px-4 py-2 text-white/80">
                      {children}
                    </td>
                  ),
                  hr: () => (
                    <hr className="border-white/10 my-8" />
                  ),
                  strong: ({ children }) => (
                    <strong className="text-white font-semibold">{children}</strong>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          )}

          {/* Navigation Footer */}
          <div className="max-w-4xl mt-12 pt-8 border-t border-white/10">
            <div className="flex justify-between items-center">
              {docSections.findIndex(s => s.id === activeSection) > 0 && (
                <button
                  onClick={() => {
                    const currentIndex = docSections.findIndex(s => s.id === activeSection);
                    setActiveSection(docSections[currentIndex - 1].id);
                  }}
                  className="flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors"
                >
                  <ArrowLeft size={16} />
                  <span>{docSections[docSections.findIndex(s => s.id === activeSection) - 1]?.title}</span>
                </button>
              )}
              <div className="flex-1" />
              {docSections.findIndex(s => s.id === activeSection) < docSections.length - 1 && (
                <button
                  onClick={() => {
                    const currentIndex = docSections.findIndex(s => s.id === activeSection);
                    setActiveSection(docSections[currentIndex + 1].id);
                  }}
                  className="flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors"
                >
                  <span>{docSections[docSections.findIndex(s => s.id === activeSection) + 1]?.title}</span>
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TritonDocsPage;
